import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../../lib/supabase';

function ClientServices() {
  const [clientServices, setClientServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showRecurringOnly, setShowRecurringOnly] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    service_type_id: '',
    is_recurring: false,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    value: '',
    notes: ''
  });

  // Calcula o total dos serviços filtrados
  const calculateTotal = () => {
    return clientServices
      .filter(service => !showRecurringOnly || service.is_recurring)
      .reduce((sum, service) => sum + (service.value || 0), 0);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Busca serviços dos clientes
      const { data: servicesData, error: servicesError } = await supabase
        .from('client_services')
        .select(`
          *,
          client:clients(company),
          service_type:service_types(name)
        `)
        .order('start_date', { ascending: false });

      if (servicesError) throw servicesError;
      setClientServices(servicesData || []);

      // Busca clientes
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, company')
        .order('company');

      if (clientsError) throw clientsError;
      setClients(clientsData || []);

      // Busca tipos de serviço
      const { data: typesData, error: typesError } = await supabase
        .from('service_types')
        .select('id, name')
        .order('name');

      if (typesError) throw typesError;
      setServiceTypes(typesData || []);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        client_id: service.client_id,
        service_type_id: service.service_id,
        is_recurring: service.is_recurring || false,
        start_date: service.start_date,
        end_date: service.end_date || '',
        value: service.value ? Math.round(service.value * 100).toString() : '', // Convertendo para centavos
        notes: service.notes || ''
      });
    } else {
      setEditingService(null);
      setFormData({
        client_id: '',
        service_type_id: '',
        is_recurring: false,
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        value: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatToCurrency = (value) => {
    if (!value) return '';
    // Remove non-numeric characters
    value = value.replace(/\D/g, '');
    // Convert to number and divide by 100 to get decimal places
    const numericValue = Number(value) / 100;
    return numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleValueChange = (e) => {
    let value = e.target.value;
    // Remove tudo exceto números
    value = value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      value: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Basic data without is_recurring for now
      const submitData = {
        client_id: formData.client_id,
        service_id: formData.service_type_id,
        value: parseFloat(formData.value) / 100, // Convertendo de centavos para reais
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        is_active: true
      };

      console.log('Submitting service:', submitData);

      let result;
      if (editingService) {
        result = await supabase
          .from('client_services')
          .update(submitData)
          .eq('id', editingService.id);

        if (result.error) {
          console.error('Error updating service:', result.error);
          throw result.error;
        }

        // If update succeeds, try to update is_recurring separately
        const recurringUpdate = await supabase
          .from('client_services')
          .update({ is_recurring: formData.is_recurring })
          .eq('id', editingService.id);

        if (recurringUpdate.error) {
          console.error('Error updating recurring status:', recurringUpdate.error);
          // Don't throw, at least the basic data was saved
        }
      } else {
        result = await supabase
          .from('client_services')
          .insert([submitData]);

        if (result.error) {
          console.error('Error inserting service:', result.error);
          throw result.error;
        }

        // If insert succeeds, try to update is_recurring separately
        if (result.data && result.data[0]) {
          const recurringUpdate = await supabase
            .from('client_services')
            .update({ is_recurring: formData.is_recurring })
            .eq('id', result.data[0].id);

          if (recurringUpdate.error) {
            console.error('Error updating recurring status:', recurringUpdate.error);
            // Don't throw, at least the basic data was saved
          }
        }
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving client service:', error);
      alert('Erro ao salvar serviço: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este serviço?')) {
      try {
        const { error } = await supabase
          .from('client_services')
          .delete()
          .eq('id', id);

        if (error) throw error;
        fetchData();
      } catch (error) {
        console.error('Error deleting client service:', error);
      }
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Serviços dos Clientes</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showRecurringOnly}
                onChange={(e) => setShowRecurringOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Mostrar apenas recorrentes"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Serviço
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Serviço</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Término</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Recorrente</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientServices
              .filter(service => !showRecurringOnly || service.is_recurring)
              .map((service) => (
                <TableRow key={service.id}>
                  <TableCell>{service.client?.company}</TableCell>
                  <TableCell>{service.service_type?.name}</TableCell>
                  <TableCell>{formatDate(service.start_date)}</TableCell>
                  <TableCell>{service.end_date ? formatDate(service.end_date) : '-'}</TableCell>
                  <TableCell>{formatCurrency(service.value)}</TableCell>
                  <TableCell>
                    <Chip
                      label={service.is_recurring ? 'Sim' : 'Não'}
                      color={service.is_recurring ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(service)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(service.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {showRecurringOnly && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Total Recorrente Mensal:
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(calculateTotal())}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingService ? 'Editar Serviço' : 'Novo Serviço'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Cliente</InputLabel>
              <Select
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                required
              >
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Serviço</InputLabel>
              <Select
                name="service_type_id"
                value={formData.service_type_id}
                onChange={handleChange}
                required
              >
                {serviceTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              name="value"
              label="Valor"
              value={formData.value ? formatToCurrency(formData.value) : ''}
              onChange={handleValueChange}
              fullWidth
              required
              sx={{ mb: 2 }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
            />

            <TextField
              fullWidth
              label="Data de Início"
              name="start_date"
              type="date"
              value={formData.start_date}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Data de Término"
              name="end_date"
              type="date"
              value={formData.end_date}
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  name="is_recurring"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    is_recurring: e.target.checked
                  }))}
                />
              }
              label="Recorrente"
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingService ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientServices;
